import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { defaultAuthToastContainerProps } from "../../config/reactToastifyDefaults";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";

import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import RoundCheck from "../../components/ui/RoundCheck";
import AuthHeroPanel from "../../components/auth/AuthHeroPanel";
import ProductionMigrationBanner from "../../components/auth/ProductionMigrationBanner";
import ProductionMigrationModal from "../../components/Modals/ProductionMigrationModal";

import { signin, completeMigrationAccountSetup } from "../../utils";
import { applySigninSession } from "../../utils/authSession";
import { loginSchema } from "../../validators/authValidators";

export default function AuthLoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [migrationModalOpen, setMigrationModalOpen] = React.useState(false);

  const handleMigrationSubmit = React.useCallback(async (payload: Record<string, unknown>) => {
    return completeMigrationAccountSetup(payload);
  }, []);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      try {
        const result = await signin(values.email, values.password);
        console.log(result);
        if (applySigninSession(result, dispatch, values.email)) {
          toast.success("Login successful!");
          setTimeout(() => navigate("/", { replace: true }), 300);
        } else {
          toast.error(result?.error || result?.msg || "Login failed");
        }
      } catch (err) {
        toast.error("Login failed");
      }
    },
  });

  return (
    <section className="flex min-h-screen flex-col lg:min-h-screen">
      <ProductionMigrationBanner onOpenMigration={() => setMigrationModalOpen(true)} />
      <ProductionMigrationModal
        isOpen={migrationModalOpen}
        onClose={() => setMigrationModalOpen(false)}
        onSubmitMigration={handleMigrationSubmit}
      />

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
      {/* Left panel */}
      <div className="bg-white w-full lg:w-[38%] flex items-center justify-center px-5 sm:px-8 xl:px-12 py-8">
        <div className="w-full">
          <button
            type="button"
            onClick={() => {
              const url = process.env.REACT_APP_WEBSITE_URL || "/";
              window.location.href = url;
            }}
            className="inline-flex"
            aria-label="Go to website"
          >
            <img src="/new-logo/logo.png" alt="Innlead" className="h-12 w-auto cursor-pointer" />
             {/* Left panel */}
          </button>

          <h2 className="mt-8 text-h4 font-semibold text-black">
            Sign In
          </h2>
          <p className="mt-2 text-sm text-black/60">
            Don&apos;t have an account?{" "}
            <Link to="/register" className="text-brand-blue font-medium underline">
              Sign up
            </Link>
          </p>

          <form onSubmit={formik.handleSubmit} className="mt-10 flex flex-col gap-5">
            <Input
              label="Email address"
              id="email"
              placeholder="Email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email ? formik.errors.email : ""}
            />

            <Input
              label="Password"
              id="password"
              type="password"
              placeholder="Enter your password"
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password ? formik.errors.password : ""}
            />

            <div className="flex items-center justify-between">
              <RoundCheck
                checked={formik.values.remember}
                onChange={(checked) => formik.setFieldValue("remember", checked)}
                label="Remember me"
              />

              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-brand-blue text-sm font-medium underline"
              >
                Forgot Password?
              </button>
            </div>

            <Button variant="primary" fullWidth size="lg" type="submit">
              Sign In
            </Button>
          </form>

          <ToastContainer {...defaultAuthToastContainerProps} />
        </div>
      </div>

      {/* Right panel */}
      <AuthHeroPanel />
      </div>
    </section>
  );
}

